import { Component } from '@theme/component';
// QuantitySelectorUpdateEvent is used for JSDoc type annotations
// eslint-disable-next-line no-unused-vars
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */
/**
 * @typedef {HTMLElement & { handleClick: (event: Event) => void }} QuickAddHostElement
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];
  static #MOBILE_MEDIA_QUERY = '(max-width: 1079px)';

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {QuickAddHostElement | null} */
  #quickAddHost = null;

  /** @type {HTMLElement | null} */
  #buyButtonsBlock = null;

  /** @type {HTMLElement | null} */
  #footer = null;

  /** @type {MutationObserver | null} */
  #rootClassObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#disableStickyBarTransitions();
    this.#setupIntersectionObserver();
    this.#observeRootClassChanges();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    // Colorway switches (product-grouping/combined listing) re-render the whole
    // `<main>` via morph and dispatch variant:update on `document` instead of the
    // variant-picker, so it never reaches the section listener above. Listen on
    // document to re-initialise the bar after that product swap.
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleProductSwitch, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, {
      signal,
    });
    window.addEventListener('resize', this.#syncStickyVisibility, { signal });

    this.#getInitialQuantity();
    this.#syncFromMainAddToCartButton();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#rootClassObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();

    const productForm = this.#getProductForm();
    if (!productForm) return;

    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    this.#syncFromMainAddToCartButton();
    this.#buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!this.#buyButtonsBlock) return;

    this.#footer = document.querySelector('footer');

    const visibilityTarget = this.#getVisibilityTarget();
    if (!visibilityTarget) return;

    // Observer for native add-to-cart button full visibility (100% in viewport)
    this.#buyButtonsIntersectionObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        const isFullyVisible = entry.isIntersecting && entry.intersectionRatio >= 1;
        if (isFullyVisible) {
          this.#hiddenByBottom = false;
          this.#hideStickyBar();
          return;
        }

        if (!this.#hiddenByBottom) {
          this.#showStickyBar();
        }
      },
      { threshold: 1.0 },
    );

    this.#buyButtonsIntersectionObserver.observe(visibilityTarget);
    if (this.#footer) {
      // Observer for footer visibility - hides sticky bar at page bottom
      this.#mainBottomObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry) return;

          if (entry.isIntersecting && this.#isStuck) {
            this.#hiddenByBottom = true;
            this.#hideStickyBar();
          } else if (!entry.isIntersecting && this.#hiddenByBottom) {
            this.#hiddenByBottom = false;
            this.#syncStickyVisibility();
          }
        },
        {
          rootMargin: '200px 0px 0px 0px',
        },
      );
      this.#mainBottomObserver.observe(this.#footer);
    }
    this.#syncStickyVisibility();
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (this.#isMobileViewport() && !this.#hasSelectedSize() && this.#openQuickAddDrawer()) {
      return;
    }

    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    if (variant?.id) {
      this.dataset.currentVariantId = String(variant.id);
    }
    if (variant && typeof variant.available === 'boolean') {
      this.dataset.variantAvailable = String(variant.available);
    } else if (variant == null) {
      this.dataset.variantAvailable = 'false';
    }

    const html = event.detail.data.html;
    if (!html || typeof html.querySelector !== 'function') {
      this.#updateButtonText();
      return;
    }

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }
    this.#setupIntersectionObserver();
    this.#syncFromMainAddToCartButton();

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Re-initialises the sticky bar after a colorway/product swap.
   *
   * On product-grouping (colorway) switches the variant-picker morphs the whole
   * `<main>` and fires variant:update on `document`. That morph replaces the
   * sticky bar markup (resetting `data-stuck` and wiping the JS-applied inline
   * display) but does not re-run `connectedCallback`, so without this the bar
   * stays hidden until the next scroll. We only act on the `document` dispatch
   * (same-product updates bubble from the variant-picker and keep their original
   * target), so the section-level handler is left untouched.
   * @param {CustomEvent} event - The variant update event
   */
  #handleProductSwitch = (event) => {
    if (event.target !== document || !event.detail?.data?.newProduct) return;
    if (event.detail.data.productId !== this.dataset.productId) return;

    this.#hiddenByBottom = false;
    this.#setupIntersectionObserver();
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
    this.#syncFromMainAddToCartButton();
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Returns true when the viewport matches mobile breakpoint.
   * @returns {boolean}
   */
  #isMobileViewport() {
    return window.matchMedia(StickyAddToCartComponent.#MOBILE_MEDIA_QUERY).matches;
  }

  /**
   * Returns true when a size input is selected in local product context.
   * @returns {boolean}
   */
  #hasSelectedSize() {
    const section = this.closest('.shopify-section');
    if (!section) return true;

    const sizeFieldset = section.querySelector(
      '[data-fieldset-value="Size"], [data-fieldset-value="Taille"]',
    );
    if (!sizeFieldset) return true;

    return Boolean(sizeFieldset.querySelector('input[type="radio"]:checked'));
  }

  /**
   * Opens the hidden quick-add drawer host and returns whether it succeeded.
   * @returns {boolean}
   */
  #openQuickAddDrawer() {
    const quickAddHost = this.#getQuickAddHost();
    if (!quickAddHost || typeof quickAddHost.handleClick !== 'function') return false;

    quickAddHost.handleClick(new MouseEvent('click', { bubbles: true, cancelable: true }));
    return true;
  }

  /**
   * Gets hidden quick-add host for sticky CTA flow.
   * @returns {QuickAddHostElement | null}
   */
  #getQuickAddHost() {
    if (this.#quickAddHost?.isConnected) return this.#quickAddHost;

    const section = this.closest('.shopify-section');
    if (!section) return null;

    this.#quickAddHost = /** @type {QuickAddHostElement | null} */ (
      section.querySelector('[data-sticky-quick-add-host] quick-add-component')
    );
    return this.#quickAddHost;
  }

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Watches root class changes so DY class toggles can resync sticky visibility.
   */
  #observeRootClassChanges() {
    if (this.#rootClassObserver) return;

    this.#rootClassObserver = new MutationObserver(() => {
      this.#syncStickyVisibility();
    });

    this.#rootClassObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  /**
   * Syncs sticky state against current viewport visibility and active experiment gate.
   */
  #syncStickyVisibility = () => {
    const visibilityTarget = this.#getVisibilityTarget();
    if (!visibilityTarget) return;

    const nativeButtonRect = visibilityTarget.getBoundingClientRect();
    const footerRect = this.#footer?.getBoundingClientRect() || null;

    const nativeButtonFullyVisible =
      nativeButtonRect.top >= 0 && nativeButtonRect.bottom <= window.innerHeight;
    const footerInViewport = footerRect
      ? footerRect.top < window.innerHeight && footerRect.bottom > 0
      : false;

    if (footerInViewport) {
      this.#hiddenByBottom = true;
      this.#hideStickyBar();
      return;
    }

    this.#hiddenByBottom = false;

    if (nativeButtonFullyVisible) {
      this.#hideStickyBar();
      return;
    }

    this.#showStickyBar();
  };

  /**
   * Returns the element used to drive sticky show/hide visibility decisions.
   * In experiment mode we track the actual PDP add-to-cart button;
   * otherwise we keep the existing buy-buttons block behavior.
   * @returns {HTMLElement | null}
   */
  #getVisibilityTarget() {
    return this.#targetAddToCartButton || this.#buyButtonsBlock;
  }

  /**
   * Removes transitions to keep sticky visibility changes immediate.
   */
  #disableStickyBarTransitions() {
    this.refs.stickyBar.style.transition = 'none';
  }

  /**
   * Shows the sticky bar without animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    stickyBar.style.display = 'flex';
  }

  /**
   * Hides the sticky bar without animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    stickyBar.style.display = 'none';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} .buy-buttons-block product-form-component[data-product-id="${productId}"]`,
    );
  }

  /**
   * Mirrors label and interaction state from the main product form button.
   */
  #syncFromMainAddToCartButton() {
    const targetAddToCartButton = this.#targetAddToCartButton;
    if (!targetAddToCartButton) return;

    const stickyButton = this.refs.addToCartButton;
    const targetButtonContainer = /** @type {HTMLElement | null} */ (
      targetAddToCartButton.closest('add-to-cart-component')
    );
    const isNotifyMeMode =
      targetAddToCartButton.hasAttribute('data-open-notify-me') ||
      targetButtonContainer?.dataset.notifyMeMode === 'true';

    if (isNotifyMeMode) {
      this.dataset.notifyMeMode = 'true';
    } else {
      delete this.dataset.notifyMeMode;
    }

    stickyButton.disabled = targetAddToCartButton.disabled;

    const stickyTextInner = stickyButton.querySelector('.add-to-cart-text__content-inner');
    const targetTextInner = targetAddToCartButton.querySelector('.add-to-cart-text__content-inner');
    if (stickyTextInner && targetTextInner) {
      stickyTextInner.replaceChildren(
        ...Array.from(targetTextInner.childNodes).map((node) => node.cloneNode(true)),
      );
    }
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const variantAvailable = this.dataset.variantAvailable === 'true';
    const isNotifyMeMode = this.dataset.notifyMeMode === 'true';
    const shouldShowQuantity = variantAvailable && !isNotifyMeMode && !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (shouldShowQuantity && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
